import ButtonLink from '@components/button-link/ButtonLink';
import HomeCard from '@components/cards/HomeCard';
import awaitUserLogin from '@components/login/awaitUserLogin';
import UserContext from '@context/UserContext';
import Sentry from '@integrations/Sentry';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { useStyles } from '@ui/utils/makeStyles';
import { addSavedHome, removeSavedHome } from '@utils/savedItems';
import { homeToSegmentEventData } from '@utils/segmentAnalytics';
import formatCurrency from 'format-currency';
import React, { useContext } from 'react';
import { A11y, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';


interface HomesCarouselProps {
  className?: string;
  style?: React.CSSProperties
  homes?: guesthouse.HomeLite[];
  seeAll?: boolean;
  usePhotosLink?: boolean;
}

const HomesCarousel: React.FC<HomesCarouselProps> = (props) => {
  const { homes, className, style, seeAll, usePhotosLink } = props;
  const { theme } = useStyles();
  const userContext = useContext<UserContext>(UserContext);
  const { md } = useBreakpoints();


  return (
    <Swiper
      navigation
      modules={[Pagination, Navigation, A11y, Mousewheel]}
      observer={true}
      observeParents={true}
      className={className}
      style={style}
      slidesPerView="auto"
      mousewheel={{
        forceToAxis: true,
      }}
      freeMode={true}
      spaceBetween={24}
      pagination={{
        clickable: true,
        type: 'progressbar',
      }}
      threshold={md ? 250 : 0}
    >
      {homes.map(home => {
        const saved = userContext.savedHomes?.ids.indexOf(home.docID) > -1;

        if (!home) {
          return;
        }

        return (
          <SwiperSlide
            key={home.docID}
            style={{ width: 'auto' }}
          >
            <HomeCard
              key={home.docID}
              home={home}
              usePhotosLink={usePhotosLink}
              style={{ marginBottom: theme.spacing(2), width: md ? 297 : 221 }}
              imageHeight={md ? 217 : 162}
              saved={saved}
              renderCta={(home, className) => {
                if (!home) {
                  return;
                }

                return (
                  <>
                    {home.listing_price && (
                      <Typography
                        className={className}
                        component="span"
                      >
                        {`Listed for ${formatCurrency(home.listing_price, {
                          format: '%s%v',
                          symbol: '$',
                          minimumFractionDigits: 0,
                        })}`}
                      </Typography>
                    )}
                  </>
                );
              }}
              onClick={() => {
                try {
                  window.analytics?.track('Home Clicked', homeToSegmentEventData(home));
                } catch (e) {
                  Sentry.captureException(e);
                }
              }}
              onSavedClick={async () => {
                if (userContext.user) {
                  if (saved) {
                    removeSavedHome(userContext.user.uid, home);
                  } else {
                    addSavedHome(userContext.user.uid, home);
                  }
                } else {
                  await awaitUserLogin(userContext)
                    .then((user) => {
                      addSavedHome(user.uid, home);
                    });
                }
              }}
            />
          </SwiperSlide>
        );
      })}

      {seeAll && (
        <SwiperSlide
          style={{ width: 'auto' }}
        >

          <div
            key="see-all"
            style={{
              width: 400,
              paddingTop: theme.spacing(20),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: theme.shape.borderRadius
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              component={ButtonLink}
              href="/shop/homes"
              size="large"
            >
              See all homes
            </Button>
          </div>

        </SwiperSlide>
      )}
    </Swiper>
  );
};

export default HomesCarousel;
